import { Dispatch, FormEventHandler, SetStateAction, useCallback, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePostManuscriptsReadFlist, usePostManuscriptsReadFlistCheck } from '../../../../api/manuscripts/manuscripts';
import { useDefaultOptions } from '../../../../hooks/api';
import FileDrop from '../../../atoms/FileDrop';
import ConfirmModal from '../../../modules/ConfirmModal';
import ErrorModal from '../../../modules/ErrorModal';
import Modal from '../../../modules/Modal';
import OkModal from '../../../modules/OkModal';
import SpinnerModal from '../../../modules/SpinnerModal';

interface Props {
  active: boolean;
  productionId: number;
  setIsFlist: Dispatch<SetStateAction<boolean | undefined>>;
  setIsOri: Dispatch<SetStateAction<boolean | undefined>>;
  onClose: () => void;
}

/**
 * F-list送信ダイアログ
 * @param param0
 * @returns
 */
function FListUploadModal({ active, productionId, setIsFlist, setIsOri, onClose }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // Form定義
  const { handleSubmit, setValue, reset } = useForm();

  // F-list再送信チェック処理定義
  const options = useDefaultOptions(language);

  const { mutateAsync: manuscriptsReadFlistCheckAsync, error: manuscriptsReadFlistCheckError } =
    usePostManuscriptsReadFlistCheck(options);

  const [displayManuscriptsReadFlistCheckError, setDisplayManuscriptsReadFlistCheckError] = useState(false);
  const [displayReadFlistOk, setdisplayReadFlistOk] = useState(false);

  useEffect(
    () => setDisplayManuscriptsReadFlistCheckError(manuscriptsReadFlistCheckError != null),
    [manuscriptsReadFlistCheckError]
  );

  // F-list送信処理定義
  const [displayManuscriptsReadFlistError, setDisplayManuscriptsReadFlistError] = useState(false);
  const { mutateAsync: manuscriptsReadFlistAsync, error: manuscriptsReadFlistError } =
    usePostManuscriptsReadFlist(options);
  useEffect(() => {
    setIsSubmitting(false);
    setDisplayManuscriptsReadFlistError(manuscriptsReadFlistError != null);
  }, [manuscriptsReadFlistError]);

  // Flistアップロード処理
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sendFlist = useCallback(() => {
    void handleSubmit(async (data: FieldValues) => {
      if (data.flistFiles?.length > 0) {
        setIsSubmitting(true);
        await manuscriptsReadFlistAsync(
          { productionId, data: { file: data.flistFiles[0] } },
          {
            // API アクセスが成功したら、ダウンロードを実行
            onSuccess: () => {
              setIsFlist(true); // f-listボタン表示
              setIsOri(false); // ori移動ボタン無効
              setdisplayReadFlistOk(true);
            },
          }
        );
      }
    })();
  }, [handleSubmit, manuscriptsReadFlistAsync, productionId, setIsFlist, setIsOri, setdisplayReadFlistOk]);

  // Flistアップロード前上書きチェック
  const [displayConfirmReloadFlist, setDisplayConfirmReloadFlist] = useState(false);

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();

      void handleSubmit(async (data: FieldValues) => {
        if (data.flistFiles?.length > 0) {
          setIsSubmitting(true);
          const response = await manuscriptsReadFlistCheckAsync({ productionId });
          setIsSubmitting(false);
          if (response.data.flist_already_loaded) {
            setDisplayConfirmReloadFlist(true);
            return;
          }
          sendFlist();
        }
      })();
    },
    [setIsSubmitting, productionId, manuscriptsReadFlistCheckAsync, sendFlist, handleSubmit]
  );

  const onCloseClicked = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  return (
    <>
      <Modal active={active} additionalClassName="popupfr800" displayCloseButton onClose={onCloseClicked}>
        <form onSubmit={onSubmit}>
          <fieldset disabled={isSubmitting} className="fieldset-reset">
            <FileDrop id="flistFiles" name="flistFiles" setValue={setValue} />
            <div className="flist-send-button">
              <button type="submit" className="text-button">
                <i className="fa-solid fa-upload" />
                {t('送信')}
              </button>
            </div>
          </fieldset>
        </form>
      </Modal>
      {isSubmitting && <SpinnerModal />}
      {displayConfirmReloadFlist && (
        <ConfirmModal
          onClickCancel={() => setDisplayConfirmReloadFlist(false)}
          onClickOk={() => {
            setDisplayConfirmReloadFlist(false);
            sendFlist();
          }}
        >
          {t('既存のデータがすべて上書きされ、手で修正した内容もクリアされますがよろしいですか？')}
        </ConfirmModal>
      )}
      {displayManuscriptsReadFlistCheckError && (
        <ErrorModal
          onClickOk={() => {
            setIsSubmitting(false);
            setDisplayManuscriptsReadFlistCheckError(false);
          }}
          error={manuscriptsReadFlistCheckError}
        />
      )}
      {displayManuscriptsReadFlistError && (
        <ErrorModal
          onClickOk={() => {
            setIsSubmitting(false);
            setDisplayManuscriptsReadFlistError(false);
          }}
          error={manuscriptsReadFlistError}
        />
      )}
      {displayReadFlistOk && (
        <OkModal
          onClickOk={() => {
            setIsSubmitting(false);
            setdisplayReadFlistOk(false);
            reset();
            onClose();
          }}
        >
          <div>{t('F-Listを読込みました。')}</div>
        </OkModal>
      )}
    </>
  );
}

export default FListUploadModal;
